import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DestinationCard from "@/components/DestinationCard";
import { toast } from "sonner";

const LABELS = {
  international: "International",
  domestic: "Domestic",
  "fixed-departures": "Fixed Departures",
  "short-trip": "Short Trip",
  adventure: "Adventure",
  devotional: "Devotional Tour",
};

const CATEGORY_SEO = {
  international: {
    title: "International Tour Packages | OITS Travel Together",
    description: "Explore international group tour packages with OITS Travel Together. Discover destinations abroad and plan your next holiday with our team.",
  },
  domestic: {
    title: "Domestic Tour Packages in India | OITS Travel Together",
    description: "Explore domestic tour packages across India with OITS Travel Together. Discover holidays, destinations and group travel options.",
  },
  "fixed-departures": {
    title: "Fixed Departure Group Tours | OITS Travel Together",
    description: "Explore fixed-departure group tours with OITS Travel Together and find a planned holiday that fits your travel dates.",
  },
  "short-trip": {
    title: "Short Trips & Getaways | OITS Travel Together",
    description: "Find short trips and getaway ideas with OITS Travel Together, for a refreshing break without a long holiday.",
  },
  adventure: {
    title: "Adventure Tour Packages | OITS Travel Together",
    description: "Explore adventure tour options with OITS Travel Together and discover new destinations and experiences.",
  },
  devotional: {
    title: "Devotional & Pilgrimage Tours | OITS Travel Together",
    description: "Explore devotional and pilgrimage tour packages with OITS Travel Together. Discover spiritual journeys and group tours to sacred destinations.",
  },
};

export default function CategoryPage() {
  const { slug } = useParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [favIds, setFavIds] = useState(new Set());
  const { user } = useAuth();

  useEffect(() => {
    const seo = CATEGORY_SEO[slug];
    if (!seo) return;

    document.title = seo.title;
    let description = document.querySelector('meta[name="description"]');
    if (!description) {
      description = document.createElement("meta");
      description.setAttribute("name", "description");
      document.head.appendChild(description);
    }
    description.setAttribute("content", seo.description);
  }, [slug]);

  // Update the category view as soon as the route changes. Keep the image/card
  // loading independent, and ignore responses from a previously selected tab.
  useEffect(() => {
    let cancelled = false;
    setItems([]);
    setLoading(true);

    api.get("/destinations", { params: { category: slug } })
      .then(({ data }) => {
        if (!cancelled) setItems(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) {
          setItems([]);
          toast.error("Unable to load destinations. Please try again.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [slug]);

  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setFavIds(new Set());
      return () => { cancelled = true; };
    }
    api.get("/favorites")
      .then(({ data }) => {
        if (!cancelled) setFavIds(new Set(data.map((d) => d.id)));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user]);

  const toggleFav = async (d) => {
    if (favIds.has(d.id)) {
      await api.delete(`/favorites/${d.id}`);
      setFavIds((current) => {
        const next = new Set(current);
        next.delete(d.id);
        return next;
      });
      toast.success("Removed from favorites");
    } else {
      await api.post("/favorites/" + d.id);
      setFavIds((current) => new Set(current).add(d.id));
      toast.success("Added to favorites");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-16">
      <div className="mb-10">
        <div className="text-xs uppercase tracking-widest text-accent mb-2">Category</div>
        <h1 className="heading-serif text-5xl">{LABELS[slug] || slug}</h1>
      </div>
      {loading ? (
        <div className="text-muted-foreground text-sm" role="status">Loading destinations…</div>
      ) : items.length === 0 ? (
        <div className="text-muted-foreground text-sm">No destinations yet in this category.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="category-grid">
          {items.map((d) => (
            <DestinationCard key={d.id} d={d} isFav={favIds.has(d.id)} onToggleFav={toggleFav} />
          ))}
        </div>
      )}
    </div>
  );
}
